import React, { useEffect, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import {
  FaWallet,
  FaArrowLeft,
  FaFileCsv,
  FaHotel,
  FaWalking,
  FaAward,
  FaChartBar,
  FaCoins
} from "react-icons/fa";
import {
  getAgentEligibleAmount,
  requestAgentWithdrawal,
  getAgentWithdrawalHistory,
  saveBankDetails,
  getWithdrawalStatus,
} from "../services/withdrawalService";
import "./AgentCommissionWithdrawal.css";
import NavUser from "../components/NavUser";

// Helper to format currency
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

// Helper to format date
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

function BankDetailsModal({ onClose }) {
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountHolderName || !bankName || !bankBranch || !accountNumber) {
      Swal.fire("Error", "All fields are required.", "error");
      return;
    }

    try {
      const token = localStorage.getItem("jwt_token");
      await saveBankDetails(
        {
          account_holder_name: accountHolderName,
          bank_name: bankName,
          bank_branch: bankBranch,
          account_number: accountNumber,
        },
        token
      );
      Swal.fire("Success", "Bank details submitted successfully!", "success");
      onClose();
    } catch (error) {
      Swal.fire(
        "Error",
        error.message || "Failed to submit bank details.",
        "error"
      );
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content premium-modal">
        <h2>Provide Bank Details</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Account Holder Name</label>
            <input
              type="text"
              name="account_holder_name"
              id="account_holder_name"
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              required
              placeholder="e.g. John Doe"
            />
          </div>
          <div className="form-group">
            <label>Bank Name</label>
            <select
              name="bank_name"
              id="bank_name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              required
            >
              <option value="">Select Bank</option>
              <option value="Bank of Ceylon (BOC)">Bank of Ceylon (BOC)</option>
              <option value="People's Bank">People's Bank</option>
              <option value="Commercial Bank">Commercial Bank</option>
              <option value="Sampath Bank">Sampath Bank</option>
              <option value="National Savings Bank (NSB)">
                National Savings Bank (NSB)
              </option>
              <option value="Seylan Bank">Seylan Bank</option>
              <option value="HNB (Hatton National Bank)">
                HNB (Hatton National Bank)
              </option>
              <option value="Regional Development Bank (RDB)">
                Regional Development Bank (RDB)
              </option>
            </select>
          </div>
          <div className="form-group">
            <label>Bank Branch</label>
            <input
              type="text"
              name="bank_branch"
              id="bank_branch"
              value={bankBranch}
              onChange={(e) => setBankBranch(e.target.value)}
              required
              placeholder="e.g. Colombo 03 Branch"
            />
          </div>
          <div className="form-group">
            <label>Account Number</label>
            <input
              type="text"
              name="account_number"
              id="account_number"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              required
              placeholder="e.g. 1234567890 (Account Number)"
            />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Submit Details
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AgentCommissionWithdrawal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [commissionDetails, setCommissionDetails] = useState([]);
  const [history, setHistory] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [lastWithdrawalAmount, setLastWithdrawalAmount] = useState(0);
  const [withdrawalStatus, setWithdrawalStatus] = useState("Pending");

  const token = localStorage.getItem("jwt_token");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [eligibleData, historyData, statusData] = await Promise.all([
        getAgentEligibleAmount(token),
        getAgentWithdrawalHistory(token),
        getWithdrawalStatus(token),
      ]);

      if (eligibleData) {
        setWithdrawalAmount(eligibleData.total || 0);
        setCommissionDetails(eligibleData.details || []);
      }

      if (historyData) {
        setHistory(historyData);
      }

      if (statusData) {
        setLastWithdrawalAmount(statusData.amount || 0);
        setWithdrawalStatus(statusData.status || "Not Available");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestWithdrawal = async () => {
    if (withdrawalAmount <= 0) {
      Swal.fire("Info", "No eligible amount for withdrawal.", "info");
      return;
    }
    try {
      const result = await requestAgentWithdrawal(token);
      if (result.message) {
        Swal.fire("Success", result.message, "success");
        fetchData();
      } else {
        throw new Error(
          result.message || "Failed to submit withdrawal request."
        );
      }
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  // Compute Lifetime Commissions Earned
  const lifetimeEarned = useMemo(() => {
    const historicalCompleted = history
      .filter((h) => h.status?.toLowerCase() === "completed" || h.status?.toLowerCase() === "complete")
      .reduce((sum, h) => sum + Number(h.amount || 0), 0);
    return historicalCompleted + Number(withdrawalAmount || 0);
  }, [history, withdrawalAmount]);

  // Compute Pending Withdrawals
  const pendingWithdrawals = useMemo(() => {
    return history
      .filter((h) => h.status?.toLowerCase() === "pending")
      .reduce((sum, h) => sum + Number(h.amount || 0), 0);
  }, [history]);

  // Compute hotel vs tour breakdown ratios
  const breakdownRatios = useMemo(() => {
    const hotelSum = commissionDetails
      .filter((c) => c.hotel_name)
      .reduce((sum, c) => sum + Number(c.amount || 0), 0);
    const tourSum = commissionDetails
      .filter((c) => c.guide_name)
      .reduce((sum, c) => sum + Number(c.amount || 0), 0);
    const total = hotelSum + tourSum;
    return {
      hotelSum,
      tourSum,
      hotelPct: total > 0 ? Math.round((hotelSum / total) * 100) : 50,
      tourPct: total > 0 ? Math.round((tourSum / total) * 100) : 50,
    };
  }, [commissionDetails]);

  // Exporter to CSV
  const handleExportCSV = () => {
    if (commissionDetails.length === 0 && history.length === 0) {
      Swal.fire("Info", "No commission data or history to export.", "info");
      return;
    }

    let csv = "Soloway Agent Earnings Report\n\n";
    
    // Active Eligible
    csv += "ELIGIBLE COMMISSIONS BREAKDOWN\n";
    csv += "Type,Partner Name,Source ID,Amount (LKR)\n";
    commissionDetails.forEach((c) => {
      const type = c.hotel_name ? "Hotel" : "Tour";
      const name = (c.hotel_name || c.guide_name || "").replace(/"/g, '""');
      const srcId = c.hotel_name ? c.booking_id : c.tour_listing_id;
      csv += `"${type}","${name}","${srcId}",${c.amount}\n`;
    });
    csv += `,,Total Eligible,${withdrawalAmount}\n\n`;

    // History
    csv += "WITHDRAWAL HISTORY\n";
    csv += "Reference Number,Request Date,Amount (LKR),Status\n";
    history.forEach((h) => {
      const ref = (h.reference_number || "—").replace(/"/g, '""');
      const date = h.requestedAt ? new Date(h.requestedAt).toLocaleDateString() : "—";
      csv += `"${ref}","${date}",${h.amount},"${h.status}"\n`;
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `soloway_agent_earnings_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const memoizedHistory = useMemo(() => history, [history]);

  if (loading) {
    return <div className="acw-loading"><p>Loading dashboard...</p></div>;
  }

  if (error) {
    return <div className="acw-page error">{error}</div>;
  }

  return (
    <div className="acw-page">
      <NavUser />
      {showBankModal && (
        <BankDetailsModal onClose={() => setShowBankModal(false)} />
      )}

      {/* Title Header area */}
      <div className="acw-head-premium">
        <div className="acw-title-area">
          <h2 className="acw-title-premium">Earnings & Analytics</h2>
          <p className="acw-subtitle">
            Manage bank accounts, review live payouts, and trace lifetime agent commissions
          </p>
        </div>
        <button className="export-csv-btn" onClick={handleExportCSV}>
          <FaFileCsv /> Export Report
        </button>
      </div>

      {/* KPI Cards Area */}
      <div className="acw-kpis-grid">
        <div className="kpi-card eligible">
          <div className="kpi-icon-wrap"><FaWallet /></div>
          <div className="kpi-details">
            <span className="kpi-title">Eligible Commission</span>
            <span className="kpi-value text-green">LKR {money(withdrawalAmount)}</span>
          </div>
        </div>

        <div className="kpi-card lifetime">
          <div className="kpi-icon-wrap"><FaAward /></div>
          <div className="kpi-details">
            <span className="kpi-title">Lifetime Earnings</span>
            <span className="kpi-value">LKR {money(lifetimeEarned)}</span>
          </div>
        </div>

        <div className="kpi-card pending">
          <div className="kpi-icon-wrap"><FaCoins /></div>
          <div className="kpi-details">
            <span className="kpi-title">Pending Withdrawals</span>
            <span className="kpi-value text-orange">LKR {money(pendingWithdrawals)}</span>
          </div>
        </div>

        <div className="kpi-card breakdown">
          <div className="kpi-icon-wrap"><FaChartBar /></div>
          <div className="kpi-details">
            <span className="kpi-title">Hotel / Tour Share</span>
            <span className="kpi-value-share">
              <span className="share-hotel">{breakdownRatios.hotelPct}%</span>
              <span className="share-slash">/</span>
              <span className="share-tour">{breakdownRatios.tourPct}%</span>
            </span>
          </div>
        </div>
      </div>

      <div className="acw-container-premium">
        {/* Left Side: Summary & Actions */}
        <div className="card-premium glass-card-premium">
          <div className="card-header-premium">
            <h3>Withdrawal Terminal</h3>
          </div>
          <div className="card-body-premium">
            <div className="action-row">
              <p className="description-text">
                Submit withdrawal request to transfer your current eligible commission balance into your linked bank account.
              </p>
              <button
                onClick={() => setShowBankModal(true)}
                className="btn btn-secondary-premium"
              >
                Provide Bank Details
              </button>
            </div>

            <hr className="divider-premium" />

            <div className="summary-amount-premium">
              <h4>Total Eligible for Withdrawal:</h4>
              <p className={withdrawalAmount > 0 ? "text-green payout-txt" : "text-orange payout-txt"}>
                LKR {money(withdrawalAmount)}
              </p>
            </div>

            {commissionDetails.length > 0 ? (
              <div className="commission-breakdown-premium">
                <h4>Eligible Items Breakdown</h4>
                <ul className="breakdown-list">
                  {commissionDetails.map((detail, index) => (
                    <li key={index} className="breakdown-item">
                      <div className="item-left">
                        {detail.hotel_name ? (
                          <span className="tag-hotel"><FaHotel /> Hotel</span>
                        ) : (
                          <span className="tag-tour"><FaWalking /> Tour</span>
                        )}
                        <span className="item-name">
                          {detail.hotel_name
                            ? `${detail.hotel_name} (Booking #${detail.booking_id})`
                            : `${detail.guide_name} (Tour #${detail.tour_listing_id})`}
                        </span>
                      </div>
                      <strong className="item-amount">LKR {money(detail.amount)}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="no-commissions-msg">No active items ready for withdrawal.</p>
            )}

            {lastWithdrawalAmount > 0 && (
              <div className="last-withdrawal-info-premium">
                <hr className="divider-premium" />
                <div className="last-payout-box">
                  <p>Last Requested Amount: <strong>LKR {money(lastWithdrawalAmount)}</strong></p>
                  <div className="status-display-premium">
                    <span>Withdrawal Status:</span>
                    <span className={`status-badge-premium status-${withdrawalStatus.toLowerCase()}`}>
                      {withdrawalStatus}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleRequestWithdrawal}
              className="btn btn-primary btn-block-premium"
              disabled={withdrawalAmount <= 0}
            >
              Request for Withdrawal
            </button>
          </div>
        </div>

        {/* Right Side: Visual Share Chart & History */}
        <div className="card-premium glass-card-premium">
          <div className="card-header-premium">
            <h3>Earnings Visualiser & History</h3>
          </div>
          <div className="card-body-premium">
            {/* Visualizer Share Chart */}
            <div className="earnings-visualizer-container">
              <h4 className="visualizer-title">Revenue Share Breakdown</h4>
              <div className="visualizer-bar-wrapper">
                <div 
                  className="bar-segment hotel" 
                  style={{ width: `${breakdownRatios.hotelPct}%` }}
                  title={`Hotel Share: ${breakdownRatios.hotelPct}%`}
                ></div>
                <div 
                  className="bar-segment tour" 
                  style={{ width: `${breakdownRatios.tourPct}%` }}
                  title={`Tour Share: ${breakdownRatios.tourPct}%`}
                ></div>
              </div>
              <div className="visualizer-labels">
                <div className="legend-item"><span className="legend-color hotel"></span> Hotels (LKR {money(breakdownRatios.hotelSum)})</div>
                <div className="legend-item"><span className="legend-color tour"></span> Tours (LKR {money(breakdownRatios.tourSum)})</div>
              </div>
            </div>

            <hr className="divider-premium" />

            <div className="withdrawal-history-title-row">
              <h3>Withdrawal History</h3>
            </div>

            <div className="history-list-premium">
              {memoizedHistory.length === 0 ? (
                <p className="no-history-msg">No withdrawal history available.</p>
              ) : (
                memoizedHistory.map((item, index) => (
                  <div key={index} className="history-item-premium">
                    <div className="history-item-header-premium">
                      <span>
                        Amount: <strong className="hist-amount">LKR {money(item.amount)}</strong>
                      </span>
                      <span className={`status-badge-premium status-${item.status?.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </div>
                    <p className="hist-requested-date">Requested: {fmtDate(item.requestedAt)}</p>

                    {item.details && item.details.length > 0 && (
                      <div className="commission-breakdown-sub-premium">
                        <h5>Itemised Commissions</h5>
                        <ul>
                          {item.details.map((detail, idx) => (
                            <li key={idx}>
                              <span>
                                {detail.hotel_name
                                  ? `Hotel: ${detail.hotel_name} (Booking #${detail.booking_id})`
                                  : `Tour: ${detail.guide_name} (Tour Booking #${detail.tour_booking_id})`}
                              </span>
                              <strong>LKR {money(detail.amount)}</strong>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
